import { FocusEvent, KeyboardEvent, useEffect, useRef } from 'react';
import { findTabbableAfter, getTabbableElements } from '../helpers/tabbableHelper';

const isTabbingPastEdge = (content: HTMLElement, backwards: boolean) => {
  const tabbables = getTabbableElements(content);
  const active = document.activeElement;
  if (backwards) return active === content || active === tabbables[0];
  if (tabbables.length === 0) return true;
  return active === tabbables[tabbables.length - 1];
};

/**
 * antd popovers neither take focus nor give it back. The content is focused when the popup opens
 * so the keyboard is inside it, Escape closes only the popup, and focus returns to whatever
 * opened it — unless the user has already moved on to somewhere else. Tabbing past either end
 * closes the popup and carries on from the opener, as if the popup sat right after it.
 */
export const usePopupFocus = <T extends HTMLElement = HTMLDivElement>(
  open: boolean,
  onClose: () => void,
) => {
  const contentRef = useRef<T>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      openerRef.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const frame = requestAnimationFrame(() => contentRef.current?.focus());
      return () => cancelAnimationFrame(frame);
    }

    const opener = openerRef.current;
    openerRef.current = null;
    const focusIsAdrift =
      document.activeElement === document.body ||
      !!contentRef.current?.contains(document.activeElement);
    if (opener?.isConnected && focusIsAdrift) opener.focus();
    return undefined;
  }, [open]);

  const leaveOnTab = (event: KeyboardEvent<HTMLElement>) => {
    const content = event.currentTarget;
    const opener = openerRef.current;
    if (!opener || !isTabbingPastEdge(content, event.shiftKey)) return;

    const destination = event.shiftKey ? opener : findTabbableAfter(opener, undefined, content);
    if (!destination) return;
    event.preventDefault();
    // Focus leaving the content closes the popup through onBlur.
    destination.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Tab') {
      // rc-dropdown closes a popup on the second Tab it hears at window level.
      event.stopPropagation();
      leaveOnTab(event);
      return;
    }
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    onClose();
  };

  const onBlur = (event: FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget;
    if (next instanceof Node && !event.currentTarget.contains(next)) onClose();
  };

  return { contentRef, onKeyDown, onBlur, tabIndex: -1 as const };
};
